import { useEffect, useReducer } from 'react';
import { DEFAULT_CHARACTER_FILLER } from './SplitInput';

const DEFAULT_PROPS = {
    validations: [],
    defaultShowCharacters: true,
    howManyCharacters: 6,
    alwaysUppercase: false,
};

const initialState = {
    value: '',
    isCompleted: false,
    notPassedValidations: new Set(),
    showCharacters: false,
    hasError: false,
};

function reducer(state, action) {
    switch (action.type) {
        case 'SAVE_VALUE':
            return { ...state, value: action.payload.text, isCompleted: action.payload.isCompleted };
        case 'SAVE_VALIDATION_STATUS':
            return {
                ...state,
                notPassedValidations: action.payload.notPassedValidations,
                hasError: action.payload.hasError,
            };
        case 'TOGGLE_SHOW_CHARACTERS':
            return { ...state, showCharacters: !state.showCharacters };
        default:
            throw new Error();
    }
}

export const useSplitInput = ({
    validations = DEFAULT_PROPS.validations,
    defaultShowCharacters = DEFAULT_PROPS.defaultShowCharacters,
    howManyCharacters = DEFAULT_PROPS.howManyCharacters,
    alwaysUppercase = DEFAULT_PROPS.alwaysUppercase,
} = DEFAULT_PROPS) => {
    const [state, dispatch] = useReducer(reducer, { ...initialState, showCharacters: defaultShowCharacters });

    const onChange = ({ text }) => {
        dispatch({
            type: 'SAVE_VALUE',
            payload: {
                text: alwaysUppercase ? text.toUpperCase() : text,
                isCompleted: text.replace(DEFAULT_CHARACTER_FILLER, '').length === howManyCharacters,
            },
        });
    };

    const triggerValidations = () => {
        const notPassedValidations = new Set();
        validations.forEach(({ name, validationFunction }) => {
            const isValidationNotPassing = validationFunction(state.value.trim().replace(DEFAULT_CHARACTER_FILLER, ''));
            isValidationNotPassing && notPassedValidations.add(name);
        });
        const hasError = notPassedValidations.size > 0;
        dispatch({ type: 'SAVE_VALIDATION_STATUS', payload: { notPassedValidations, hasError } });
    };

    useEffect(() => {
        triggerValidations();
    }, [state.value]);

    const toggleShowCharacters = () => {
        dispatch({ type: 'TOGGLE_SHOW_CHARACTERS' });
    };

    const resetValue = () => {
        onChange({ text: DEFAULT_CHARACTER_FILLER.repeat(howManyCharacters) });
    };

    return {
        value: state.value,
        showCharacters: state.showCharacters,
        hasError: state.hasError,
        isCompleted: state.isCompleted,
        notPassedValidations: state.notPassedValidations,
        onChange,
        toggleShowCharacters,
        resetValue,
    };
};
